import { AccosListPageFilterLocalStorage, EnquiryPageName, FilterPageName, ValidLanguages, VersionLocalStorage } from '@/constants'
import { broadcasterAccosBarStore, date, initAccosBarStoreFromLocalStorage, messageReceiverAccosBarStore } from '@/store/accosbar.store'
import { initEnquiryBarOriginFromLocalStorage } from '@/store/enquiry.store'
import { broadcasterFilterStore, filtersData, initFilterStoreFromLocalStorage, messageReceiverFilterStore } from '@/store/filter.store'
import { version } from '../../package.json'
import t from '../i18n/translations'
import { messageReceiverLandingPagePackageId, messageReceiverLandingPageRoomId } from './enquiry.helpers'
import type { IAccoItem } from './interfaces/apiInterfaces'
import type { IFilterStoreInterface, IPrimeVueLocale, IRoom, IRoomDto, IRoomTypeDto } from './interfaces/appInterfaces'
import { useFilterApi } from './useApi'
export let PID: number
export let CULTURE: string
export let AREAID: number | null
export let MAP_ID: string | undefined
export let MAP_KEY: string | undefined
export let CID: number | undefined // is only used in enquirypage for landingpage
export let ACCOSLISTPAGEFILTER: IFilterStoreInterface | undefined // is only used in enquirybar
export let ENABLEMINIACCOSBAR: boolean | undefined
export let THEMEID: number | undefined

export function setGlobalVars(
  pid: number,
  l: string,
  areaid: number | undefined,
  mapkey: string | undefined,
  mapid: string | undefined,
  cid: number | undefined,
  accosListPageFilter: string | undefined,
  enablemini: boolean | undefined,
  themeid: number | undefined
): void {
  PID = pid
  CULTURE = l
  if (areaid) AREAID = areaid
  if (mapkey) MAP_KEY = mapkey
  if (mapid) MAP_ID = mapid
  if (cid) CID = cid
  if (accosListPageFilter) {
    ACCOSLISTPAGEFILTER = JSON.parse(accosListPageFilter) as IFilterStoreInterface
    localStorage.setItem(AccosListPageFilterLocalStorage, accosListPageFilter)
  }
  if (enablemini) ENABLEMINIACCOSBAR = enablemini
  if (themeid) THEMEID = themeid
}

export function toFormattedStringDateShort(): string | null {
  if (date.value != null && date.value[0] && date.value[1] != null) {
    const startDate = date.value[0]
    const endDate = date.value[1]
    return `${formatDateShort(startDate)} - ${formatDateShort(endDate)}`
  }
  return null
}

export function toSmallStringDate(): string | null {
  let returnValue = null
  try {
    if (date.value != null && date.value[0] && date.value[1] != null) {
      const startDate = date.value[0]
      const endDate = date.value[1]

      const formatDate = (date: Date) => {
        const day = date.toLocaleString(CULTURE, { day: '2-digit' })
        const month = date.toLocaleString(CULTURE, { month: '2-digit' })
        const year = date.toLocaleString(CULTURE, { year: '2-digit' })
        return `${day}.${month}.${year}`
      }

      returnValue = `${formatDate(new Date(startDate))} - ${formatDate(new Date(endDate))}`
    }
  } catch (error) {
    returnValue = null
  }

  return returnValue
}

function formatDateShort(date: Date): string {
  const dayOfWeek = date.toLocaleString(CULTURE, { weekday: 'short' })
  const day = date.toLocaleString(CULTURE, { day: '2-digit' })
  const month = date.toLocaleString(CULTURE, { month: 'short' })

  return `${dayOfWeek}., ${day} ${month}.`
}

export function toFormattedStringDateLong(): string | null {
  if (date.value != null && date.value[0] && date.value[1] != null) {
    const startDate = date.value[0]
    const endDate = date.value[1]
    return `${formatDateLong(startDate)} - ${formatDateLong(endDate)}`
  }
  return null
}

function formatDateLong(date: Date): string {
  const dayOfWeek = date.toLocaleString(CULTURE, { weekday: 'short' })
  const day = date.toLocaleString(CULTURE, { day: '2-digit' })
  const month = date.toLocaleString(CULTURE, { month: 'long' })
  const year = date.toLocaleString(CULTURE, { year: 'numeric' })

  return `${dayOfWeek}, ${day}. ${month} ${year}`
}

export function getTotalOccupancyAmount(rooms: Array<IRoom> | null): { adults: number; children: number } | null {
  if (!rooms) {
    return null
  }
  return rooms.reduce((accumulator: any, currentValue: any) => {
    accumulator = {
      adults: accumulator.adults + currentValue.adults,
      children: accumulator.children + currentValue.children
    }
    return accumulator
  })
}

export function toFormattedRoomStringShort(rooms: any, l: string): string | null {
  const summedRoomValues = getTotalOccupancyAmount(rooms)

  if (summedRoomValues != null) {
    let result = `${summedRoomValues.adults} ${t(`${l}.occupancy.adults`)}`

    if (summedRoomValues.children > 0) {
      const childrenTranslation = summedRoomValues.children === 1 ? t(`${l}.occupancy.child`) : t(`${l}.occupancy.children`)

      result += `, ${summedRoomValues.children} ${childrenTranslation}`
    }

    return result
  }
  return null
}

export function toFormattedRoomStringLong(rooms: any, l: string): string | null {
  const summedRoomValues = getTotalOccupancyAmount(rooms)

  if (summedRoomValues != null) {
    let chilrenTranslation = ''
    switch (summedRoomValues.children) {
      case 1:
        chilrenTranslation = t(`${l}.occupancy.child`)
        break
      default:
        chilrenTranslation = t(`${l}.occupancy.children`)
        break
    }

    return `${summedRoomValues.adults} ${t(`${l}.occupancy.adults`)} - ${summedRoomValues.children} ${chilrenTranslation} - ${rooms.length} ${t(`${l}.occupancy.rooms`)}`
  }
  return null
}

export function setLocale(l: string): IPrimeVueLocale | null {
  let locale = null
  switch (l) {
    case 'de':
      locale = {
        emptySearchMessage: 'Keine Ergebnisse gefunden',
        firstDayOfWeek: 1,
        dayNames: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
        dayNamesShort: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
        dayNamesMin: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
        monthNames: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
        monthNamesShort: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']
      }
      break
    case 'it':
      locale = {
        emptySearchMessage: 'Nessun risultato trovato',
        firstDayOfWeek: 1,
        dayNames: ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'],
        dayNamesShort: ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'],
        dayNamesMin: ['Do', 'Lu', 'Ma', 'Me', 'Gi', 'Ve', 'Sa'],
        monthNames: ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'],
        monthNamesShort: ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic']
      }
      break
    case 'en':
      locale = {
        emptySearchMessage: 'No results found',
        firstDayOfWeek: 1,
        dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
        dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
        monthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
        monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      }
      break
    case 'fr':
      locale = {
        emptySearchMessage: 'Aucun résultat trouvé',
        firstDayOfWeek: 1,
        dayNames: ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'],
        dayNamesShort: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
        dayNamesMin: ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'],
        monthNames: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'],
        monthNamesShort: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc']
      }
      break
    case 'nl':
      locale = {
        emptySearchMessage: 'Nie znaleziono wyników',
        firstDayOfWeek: 1,
        dayNames: ['Zondag', 'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag'],
        dayNamesShort: ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'],
        dayNamesMin: ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'],
        monthNames: ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'],
        monthNamesShort: ['Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec']
      }
      break
    case 'pl':
      locale = {
        emptySearchMessage: 'Geen resultaten gevonden',
        firstDayOfWeek: 1,
        dayNames: ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota'],
        dayNamesShort: ['Nie', 'Pon', 'Wto', 'Śro', 'Czw', 'Pią', 'Sob'],
        dayNamesMin: ['Ni', 'Po', 'Wt', 'Śr', 'Cz', 'Pi', 'So'],
        monthNames: ['Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec', 'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'],
        monthNamesShort: ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru']
      }
      break
    default:
      break
  }
  return locale
}

export function getRoomName(room: IRoomDto | undefined, culture: string): string | null {
  if (room) {
    switch (culture) {
      case 'de':
        return room.nameDe
      case 'it':
        return room.nameIt
      case 'en':
        return room.nameEn
      default:
        return room.nameEn
    }
  }
  return null
}

export function getRoomType(roomType: IRoomTypeDto | undefined, culture: string): string | null {
  if (roomType) {
    switch (culture) {
      case 'de':
        return roomType.nameDe
      case 'it':
        return roomType.nameIt
      case 'en':
        return roomType.nameEn
      default:
        return roomType.nameEn
    }
  }
  return null
}

export function handleInvalidLanguage(l: string): void {
  const bool = ValidLanguages.includes(l)
  if (bool == false) throw new Error(`not supported language: ${l}`)
}

export function uspsOrShortDescription(item: IAccoItem): boolean {
  return item.usps[0].length === 0 ? true : false
}

export function setVersionInLocalStorage(): void {
  localStorage.setItem(VersionLocalStorage, version)
}

export function delteLocalStorageIfOldVersion(): void {
  var localStorageVersion = localStorage.getItem(VersionLocalStorage)

  if (localStorageVersion != version) {
    console.log('deleting pui localstorage')
    localStorage.clear()
  }
}

export const broadcastChannelMessages = {
  UPDATE: 'update',
  ACCOSBARUPDATE: 'accosbarupdate',
  REFRESHLOCATIONFROMFILTERSTORE: 'refreshlocationfromfilterstore'
}

export async function ensureStoresAreFilled(componentType: string): Promise<void> {
  initFilterStoreFromLocalStorage()
  initAccosBarStoreFromLocalStorage()
  initEnquiryBarOriginFromLocalStorage()
  if (componentType == FilterPageName || componentType == EnquiryPageName) {
    filtersData.value = (await useFilterApi()).data
  }
}

export function garbageCollectBroadcastChannels(): void {
  broadcasterFilterStore.close()
  messageReceiverFilterStore.close()

  broadcasterAccosBarStore.close()
  messageReceiverAccosBarStore.close()

  messageReceiverLandingPageRoomId.close()

  messageReceiverLandingPagePackageId.close()
}

declare global {
  interface Window {
    siteEventEnquiry: any
    siteEventEnquiryLandingPage: any
    siteEvent?: (cat: 'button' | (string & {}), act: 'micropage' | 'enquiry-direct' | 'enquiry-success-extend' | (string & {}), lab: string) => void
    scom: Scom
  }

  interface Scom {
    correlationId: string
    language: string
    environmentName: string
    jsMaps: string
    privacyLink: string | null
    imprintLink: string | null
    abtest_value: string
  }
}

declare module 'vue' {
  interface ComponentCustomProperties {
    $window: Window
  }
}
